import { useEffect, useState, useRef, useContext, useCallback } from "react"; 
import Message from "./Message";   
import socket from "../services/socket";
// import { useSignal } from "@preact/signals-react"; // Temporarily remove for testing
import { useSignals } from "@preact/signals-react/runtime";
import { PaperAirplaneIcon, PaperClipIcon, XMarkIcon, MagnifyingGlassIcon, HomeIcon, PencilSquareIcon, InformationCircleIcon, CreditCardIcon } from "@heroicons/react/24/outline"; 
import { ChatContext } from "../context/ChatContext"; 
import SideChatAccount from "./SideChatAccount";
import Resizer from "react-image-file-resizer";
import IconButton from "./IconButton";
import CustomerSearchModal from "./CustomerSearchModal"; // Import the new modal
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
// Import the notification sound file directly
import notificationSound from "../assets/notification.mp3";

const MessageScreen = () => {
    // useSignals(); // Temporarily remove for testing
    const { data, dispatch } = useContext(ChatContext); 
    const [currentChat, setCurrentChat] = useState(data.currentChat);
    // const messages = useSignal([]); // Temporarily replace with useState
    const [messages, setMessages] = useState([]); 
    const [text, setText] = useState("");
    const inputFile = useRef(null); 
    const [file, setFile] = useState(); 
    const [isTyping, setIsTyping] = useState(false); 
    const [preview, setPreview] = useState(false);
    const [department, setDepartment] = useState(1);
    const messageSound = useRef(new Audio(notificationSound));  
    const [accountNo, setAccountNo] = useState(data.accountNo);
    const [user, setUser] = useState(data.user);
    const [navigation, setNavigation] = useState(data.navigation);
    const [customer, setCustomer] = useState(data.customer);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false); // State for modal visibility

    // Effect for synchronizing non-socket related local state from context
    useEffect(() => {
    setCurrentChat(data.currentChat); 
    setAccountNo(data.accountNo);
    setUser(data.user);
    setNavigation(data.navigation);
    setCustomer(data.customer);
    }, [data.currentChat, data.accountNo, data.user, data.navigation, data.customer]);

    useEffect(() => {   
    const currentChannelId = data.currentChat?.channel_id;

    // If there's no valid current channel, don't set up listeners.
    if (!currentChannelId || currentChannelId === 0) {
        setMessages([]); // Clear messages if no chat is active
        //console.log("MessageScreen EFFECT: No valid currentChannelId, listeners not set.");
        return; // Exit early
    }

    //console.log(`MessageScreen EFFECT RUNNING for channel: ${currentChannelId}`);

    const handleChannelMessages = (channel_messages) => {   
      //console.log("handleChannelMessages called", channel_messages);
      const messagesToShow = Array.isArray(channel_messages) ? channel_messages : [];
      setMessages(messagesToShow); 

      if (messagesToShow.length > 0) {
        // Find an incoming message to get the customer's userId for SET_ACCOUNT_NO
        const incomingMessageForAccountNo = messagesToShow.find(msg => msg.direction === 0 && msg.userId);
        if (incomingMessageForAccountNo) {
          dispatch({ type: "SET_ACCOUNT_NO", payload: incomingMessageForAccountNo.userId });
        }

        // Use the name from the first message for SET_CUSTOMER, or a fallback
        // This could also be from an incoming message if preferred.
        const messageForCustomerName = messagesToShow[0]; // Or find an incoming one specifically
        if (messageForCustomerName && typeof messageForCustomerName.name === 'string' && messageForCustomerName.name.trim() !== '') {
          dispatch({ type: "SET_CUSTOMER", payload: messageForCustomerName.name });
        }

        let last = messagesToShow[messagesToShow.length - 1];
        if (last && typeof last === 'object' && last.status === 0) { 
          socket.emit("read-message", last);   
        }
      }
    };

    const handleMessagesRead = (message) => {  
      setMessages(prevMessages => {
        if (Array.isArray(prevMessages) && message && message.channel_id !== undefined) {
          return prevMessages.map((item) =>
            (item && item.channel_id === message.channel_id)
              ? { ...item, state: message.state }
              : item
          );
        }
        return prevMessages;
      });
    };

    const handleReceiveMessage = (new_message) => {   
      if (new_message && typeof new_message === 'object') {
        // Play sound & mark as read only if the message is for the currently active chat
        if (new_message.channel_id === currentChannelId) { 
            setMessages(prevMessages => Array.isArray(prevMessages) ? [...prevMessages, new_message] : [new_message]);
            messageSound.current
              .play()
              .catch((e) => console.warn("Autoplay blocked", e));
            socket.emit("read-message", new_message); 
        }
      } else {
        console.warn("Received invalid new_message:", new_message);
      }
    };
    
    socket.on("channel-messages", handleChannelMessages);
    socket.on("messages-read", handleMessagesRead);
    socket.on("receive-message", handleReceiveMessage);  

    return () => { 
      //console.log(`MessageScreen CLEANUP for channel: ${currentChannelId}`);
      socket.off("channel-messages", handleChannelMessages);
      socket.off("messages-read", handleMessagesRead); 
      socket.off("receive-message", handleReceiveMessage);
      // socket.off("status-change"); // If you have a status-change listener, ensure it's also named and cleaned up
    };
 
 
  }, [data.currentChat?.channel_id, dispatch]);

  // Moved getData and its triggering useEffect from Home.jsx
  const getData = useCallback(async (account_number, userNameForComment) => {
    let nav = [{ name: "Home", action: "SHOW_ADDRESS", icon: HomeIcon }];

    const date = new Date();
    // const hours = date.getHours(); // Unused
    let dd = String(date.getDate()).padStart(2, '0');
    let mm = String(date.getMonth() + 1).padStart(2, '0'); 
    let yyyy = date.getFullYear();
    let today = yyyy + "-" + mm + "-" + dd;

    const formData = new FormData();
    formData.append('authenticated', true);
    formData.append('account_number', account_number);

    let customer_data = {};
    const API_URL = 'https://portal.silulumanzi.com/login/bridge/';

    const baseAccountDataStructure = {
      "account": {},
      "balance": 0,
      "balance_data": {},
      "comments": [],
      "connection_status": 0,
      "location": [], 
      "transactions": []
    };

    try {
      const response = await axios.post(API_URL, formData);
      if (response.data.status == 1) { 
        let apiResponseData = response.data;
        customer_data['account'] = apiResponseData.account;

        const formData1 = new FormData();
        formData1.append('getBalance', true);
        formData1.append('account_number', account_number);
        formData1.append('billing_date', today);

        const balanceRes = await axios.post(API_URL, formData1);
        if (balanceRes.data.status == 1) {
          nav.push({ name: "Transactions", action: "SHOW_TRANSACTIONS", icon: CreditCardIcon });
          customer_data['balance_data'] = balanceRes.data;
          customer_data['balance'] = parseFloat(balanceRes.data.last_statement.CL_BLNCE).toFixed(2);  
          
          let transactions = [];
          const formData5 = new FormData();
          formData5.append('getTransactions', true);
          formData5.append('account_number', account_number);  
          const transactionsApiResponse = await axios.post(API_URL, formData5);
          if(transactionsApiResponse.data.status == 1) {   
            let bal = null; // Initialize bal for balance calculation
            transactionsApiResponse.data.transactions.forEach((item, index) =>{ 
              const type = item.trns_type < 50 ? 'Credit' : 'Debit';
              const vat = (item.vat_amount == "NULL" || item.vat_amount == undefined || item.vat_amount == '') ? '' : 'R'+parseFloat(item.vat_amount).toFixed(2);
              
              if(index === 0) {                              
                bal = parseFloat(item.OP_BLNCE) + parseFloat(item.amount);
              } else {
                bal = parseFloat(bal) + parseFloat(item.amount); 
              }   
              const balance = 'R'+parseFloat(bal).toFixed(2);                                          
              const effect_date = item.effect_dte.substring(0, 10);               
              const amount = 'R'+parseFloat(item.amount).toFixed(2);  
              transactions.push({
                No : index + 1, 
                Date :effect_date, 
                Type: type,
                Description: item.trns_stitl,
                Amount: amount,
                Vat: vat,
                Balance: balance
              }); 
            });
            customer_data['transactions'] = transactions;
          }
        }

        const formData2 = new FormData();
        formData2.append('comments', true);
        formData2.append('account_number', account_number);
        formData2.append('comment_user', userNameForComment === "System" ? "Billy Metiso" : userNameForComment);
 
        const commentsRes = await axios.post(API_URL, formData2);
        if (commentsRes.data.status == 1) {
          nav.push({ name: "Comments", action: "SHOW_COMMENTS", icon: PencilSquareIcon });
        }
        if (commentsRes.data.comments) {
            customer_data['comments'] = commentsRes.data.comments.reverse();  
        }
        if (commentsRes.data.user && typeof commentsRes.data.user === 'object') {
          customer_data['edams_user'] = commentsRes.data.user.USER_NAME; 
        }

        const formData3 = new FormData();
        formData3.append('getAccountStatus', true);
        formData3.append('account_number', account_number);

        const statusRes = await axios.post(API_URL, formData3);
        if (statusRes.data.status == 1) {
          nav.push({ name: "Connection", action: "SHOW_STATUS", icon: InformationCircleIcon });
          if (statusRes.data.account_status?.CON_STATUS == 1) {
            customer_data['connection_status'] = 1;
          } else if (statusRes.data.account_status?.CON_STATUS == 2) {
            customer_data['connection_status'] = 2;
          }
        }
      }
      dispatch({ 
        type: "SET_ACCOUNT_DATA", 
        payload: { 
          fetchedData: { ...baseAccountDataStructure, ...customer_data },
          fetchedForAccountNo: account_number
        } 
      });
      dispatch({ type: "SET_NAVIGATION", payload: nav }); 
    } catch (error) {
      console.error("Error in getData (MessageScreen):", error);
      // Optionally dispatch an error state or a notification
      dispatch({ type: "FINISH_ACCOUNT_LOADING_WITHOUT_DATA" }); // Ensure loading state is cleared on error
    }
  }, [dispatch]);

  useEffect(() => {
    const userNameForComment = data.user?.name; // Use user name from context
    console.log(
      `MessageScreen DATA_FETCH_EFFECT: accountNo=${data.accountNo}, isAccountDataLoading=${data.isAccountDataLoading}, userName=${userNameForComment}, data.user exists=${!!data.user}`
    );

    if (!data.isAccountDataLoading) {
      console.log("MessageScreen DATA_FETCH_EFFECT: Not in an active loading cycle (isAccountDataLoading is false). No action.");
      return; // Not in a loading cycle, so do nothing.
    }

    // At this point, data.isAccountDataLoading is TRUE.
    console.log("MessageScreen DATA_FETCH_EFFECT: `data.isAccountDataLoading` is TRUE.");

    // Check if all necessary data is available to proceed with fetching
    if (data.accountNo != null && data.accountNo !== 0 && userNameForComment && userNameForComment !== "") {
      console.log(`MessageScreen DATA_FETCH_EFFECT: All prerequisites MET (accountNo, userName). accountNo: ${data.accountNo}`);
      const shouldFetchAccountData = data.accountNo.toString().startsWith("921");
      if (shouldFetchAccountData) {
        console.log(`MessageScreen DATA_FETCH_EFFECT: ==> Eligible ("921") and calling getData for account: ${data.accountNo}`);
        getData(data.accountNo, userNameForComment);
        // getData will dispatch SET_ACCOUNT_DATA which sets isAccountDataLoading = false
      } else {
        console.log(`MessageScreen DATA_FETCH_EFFECT: AccountNo (${data.accountNo}) NOT eligible. Dispatching FINISH_ACCOUNT_LOADING_WITHOUT_DATA.`);
        dispatch({ type: "FINISH_ACCOUNT_LOADING_WITHOUT_DATA" });
      }
    } else {
      // Prerequisites for fetching are not met (accountNo or userName is missing/invalid).
      console.log(`MessageScreen DATA_FETCH_EFFECT: Prerequisites NOT MET. accountNo=${data.accountNo}, userName=${userNameForComment}. Waiting for all data.`);
      // The effect will re-run when data.accountNo or data.user changes.
    }
  }, [data.accountNo, data.user, data.isAccountDataLoading, getData, dispatch]);

   const HandleSubmit = async (event) => {
    event.preventDefault();
   if (text.trim()) {     

      socket.emit("set-channel", currentChat.channel_id); 
      const message = {
        timestamp: Math.floor(Date.now() / 1000), 
        userId: user.id,
        name: user.name,
        message: text,
        channelId: currentChat.channel_id, 
        direction: 1,
        category: currentChat.category, 
        department: department,
        id: uuidv4()
      }   

      socket.emit("send-message", message);
      
      // Reset states after sending
      setText("");
      setIsTyping(false);

      if (preview) { // If the message sent was an image from preview
        setFile(null);      // Clear the file state (used for <img src={file}/>)
        setPreview(false);  // Hide the preview UI
        if (inputFile.current) {
          //console.log("HandleSubmit: Resetting inputFile.current.value from:", inputFile.current.value);
          inputFile.current.value = null; // CRITICAL: Reset the actual file input
        }
      }
      //socket.emit("stop-typing"); 
    } 
  };


    const handleFile = async (event) => {
      const selectedFile = event.target.files && event.target.files[0];
      //console.log("handleFile: event.target.files:", event.target.files, "selectedFile:", selectedFile);

      if (selectedFile) { // Check if a file was actually selected
        const fileName = selectedFile.name.toLowerCase(); // Work with lowercase for easier checks
        if (fileName.endsWith(".png") || // Use endsWith for more precise type checking
            fileName.endsWith(".jpeg") ||
            fileName.endsWith(".jpg") ||
            fileName.endsWith(".gif")) {

          try {
            Resizer.imageFileResizer(
              selectedFile,
              600, // maxWidth
              600, // maxHeight
              "JPEG", // compressFormat
              100, // quality
              0, // rotation
              (uri) => {
                setText(uri); // Set text to base64 for sending
                setFile(uri); // Set file for preview
                setPreview(true);
              },
              "base64", // outputType
              500, // minWidth (optional, can be 0)
              500  // minHeight (optional, can be 0)
            );
          } catch (err) {
            console.error("Error resizing image:", err);
            alert("Error processing image.");
            if (inputFile.current) { // Reset input on error
                //console.log("handleFile (resize error): Resetting inputFile.current.value from:", inputFile.current.value);
                inputFile.current.value = null;
            }
          }
        } else {
          alert("Allowed File Types are: JPEG, PNG, GIF, JPG");
          if (inputFile.current) { // Reset input if file type is not allowed
            //console.log("handleFile (invalid type): Resetting inputFile.current.value from:", inputFile.current.value);
            inputFile.current.value = null;
          }
        }
      } else {
        //console.log("File selection cancelled or no file selected.");
        // Explicitly reset the input if the dialog was cancelled
        if (inputFile.current) {
            //console.log("handleFile (no file selected/cancelled): Resetting inputFile.current.value from:", inputFile.current.value);
            inputFile.current.value = null;
        }
      }
    }

    const onFileClick = () => { 
      //console.log("onFileClick: Triggering click on inputFile.current. Current value:", inputFile.current?.value);
      // Ensure it's reset *before* click, just in case.
      if (inputFile.current) inputFile.current.value = null; 
      inputFile.current.click();
    };  
 
 
  return (
    <div className="flex h-full w-full"> {/* Parent flex container for horizontal layout */}
      {/* Main content area (messages and input form) */}
      <div className="flex flex-col flex-grow"> {/* This container will grow horizontally and stack its children vertically. */}
        {/* Chat Header - Fixed height */}
        <div className="h-16 flex items-center px-6 py-2 border-b border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between w-full"> {/* Ensured full width for justify-between */}
            {/* Left Section: Customer Info */}
            <div className="flex flex-col"> {/* Explicitly stack customer name and account number */}
              <h2 className="text-lg font-semibold text-gray-800 leading-tight">{ customer }</h2>
              <p className="text-sm text-gray-500 leading-tight">
                Account No: {data['accountNo'].toString().substring(0,3) == "921" ? data.accountNo : "N/A"}
              </p>
            </div>
            {/* Right Section: Agent Info */}
            <div className="text-sm text-gray-600">
              <span className="font-medium">{user.name || "Your Name"}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col flex-grow w-full py-5 px-5 overflow-y-auto bg-gray-50"> {/* Message list area: grows vertically and scrolls, light gray background */}
          {/* {Array.isArray(messages.value) ?  // Adjust for useState
          messages.value.map((message, id) => ( */}
          {Array.isArray(messages) && messages.map((message, id) => ( 
            <Message key={id} message={message} /> 
          ))}
        </div>
        {/* Input form area */}
        <form onSubmit={HandleSubmit} className="w-full flex items-center gap-3 px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
      { preview ? 
      <div className="flex flex-col items-center w-full p-2 border border-gray-200 rounded-lg bg-gray-50">
        <img src={file} alt="Preview" className="max-h-40 rounded-md mb-2 object-contain"/>
        <div className='flex gap-2'>
          <IconButton 
            type="submit"
            className="!bg-emerald-500 hover:!bg-emerald-600 text-white rounded-lg p-2 shadow hover:shadow-md transition-all"
          >
              <PaperAirplaneIcon className="h-6 w-6 text-white" />
          </IconButton> 
          <IconButton 
            onClick={() => { 
              //console.log("Preview cancel: Resetting inputFile.current.value from:", inputFile.current?.value);
              setFile(null); setText(""); setPreview(false); if(inputFile.current) inputFile.current.value = null; 
            }}
            className="!bg-gray-300 hover:!bg-gray-400 text-gray-700 rounded-lg p-2 shadow hover:shadow-md transition-all"
          > 
             <XMarkIcon className="h-6 w-6 text-gray-700 hover:text-red-600"/>
          </IconButton>
        </div>
      </div> : <>
      <input type='file' id='file' ref={inputFile} style={{display: 'none'}} onChange={handleFile}/> 
      {/* Temporarily replace IconButton with a direct button for debugging file input trigger */}
      <button
        type="button" // Prevents form submission if it were a submit type
        onClick={onFileClick} 
        className="p-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white transition-colors" // Basic styling
      >
        <PaperClipIcon className="h-6 w-6" />
      </button>
          <input
            type="text"
            placeholder="Type here"
            className="flex-grow p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-shadow"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <IconButton 
            type="submit" // Use type="submit" to trigger form's onSubmit
            disabled={!text.trim()} 
            className={`transition-colors ${text.trim() ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`}  
          >
            <PaperAirplaneIcon className={`h-6 w-6 ${text.trim() ? 'text-white' : 'text-gray-500'}`} />
          </IconButton>
          </>
           }
        </form>
      </div>
      {/* Sidebar area - rendered conditionally */}
 
        <div className="flex flex-col w-80 h-full flex-shrink-0 bg-white shadow-lg border-l border-gray-200"> {/* Sidebar: increased width, stronger shadow */}
            {/* Top navigation icon bar */}
            <nav
              aria-label="Sidebar Navigation"
              className="h-16 flex flex-row justify-around items-center px-4 bg-gray-50 shadow-sm border-b border-gray-200" // Added border for visual consistency
            >
              {/* Search Icon */}
               <div
                onClick={() => setIsSearchModalOpen(true)}
                title="Search Customer"
                className="flex items-center justify-center p-2 rounded-md cursor-pointer transition-colors duration-150 group hover:bg-sky-100"
              >
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-500 group-hover:text-sky-600" aria-hidden="true" />
              </div>
   
              {navigation.map((item) => (
                <div 
                  onClick={() => dispatch({ type: "SET_TAB", payload: item.name })}
                  key={item.name}
                  title={item.name} // Tooltip for accessibility
                  className={`flex items-center justify-center p-2 rounded-md cursor-pointer transition-colors duration-150 group
                              ${item.name === data.tab ? "bg-sky-500" : "bg-transparent hover:bg-sky-500"}`}  
                >
                  <item.icon
                    className={`h-5 w-5 transition-colors duration-150 
                                ${item.name === data.tab ? "text-white" : "text-gray-500 group-hover:text-white"}`}
                    aria-hidden="true"
                  />
                  <span className="sr-only">{item.name}</span>
                </div>
              ))}
            </nav> 
            <div className="flex-grow overflow-y-auto">
                <SideChatAccount key={data.accountNo || 'initial'} /> {/* Add key prop */}
            </div>
        </div> 
        <CustomerSearchModal 
          isOpen={isSearchModalOpen} 
          onClose={() => setIsSearchModalOpen(false)} 
        />
    </div>
  );
};

export default MessageScreen;
